import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Button, Spinner } from "react-bootstrap";
import { ordersApi, supplierApi } from "../services/services";
import { Order } from "../types";
import { useRequiredAuth } from "./shared/AuthContext";

export interface SupplierInfo {
  businessDisplayName?: string;
  businessAddress?: string;
}

interface DeliveryNoteReceiptProps {
  order: Order;
  formData: Record<string, any>;
  supplierInfo: SupplierInfo | null;
  fallbackSupplierName?: string;
  pageBreakBefore?: boolean;
}

export const DeliveryNoteReceipt: React.FC<DeliveryNoteReceiptProps> = ({
  order,
  formData,
  supplierInfo,
  fallbackSupplierName,
  pageBreakBefore = false,
}) => {
  const formatDateTime = (dateStr: string | undefined) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Convert "HH:MM" (24h) from the form's time field into "hh:mm AM/PM".
  const formatRequiredTime = (timeStr: string | undefined): string => {
    if (!timeStr || typeof timeStr !== "string") return "";
    const match = timeStr.match(/^(\d{1,2}):(\d{2})/);
    if (!match) return "";
    const h24 = parseInt(match[1], 10);
    const mm = match[2];
    if (isNaN(h24)) return "";
    const period = h24 >= 12 ? "PM" : "AM";
    const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
    return `${String(h12).padStart(2, "0")}:${mm} ${period}`;
  };

  const formatDateOnly = (dateStr: string | undefined) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatRequiredDateTime = (dateStr: string | undefined, timeStr: string | undefined) => {
    const datePart = formatDateOnly(dateStr);
    const timePart = formatRequiredTime(timeStr);
    if (!datePart) return "";
    return timePart ? `${datePart}, ${timePart}` : datePart;
  };

  const branch = formData.branch || order.clientName || order.branch || "";
  const orderTakenBy = formData.orderTakenBy || order.orderTakenBy || "";
  const notes = formData.requestNote || formData.requestOrNote || formData.notes || order.notes || "";
  const orderType = order.type || "";

  const isCelebrationCake = orderType === "celebration-cake" || orderType === "Celebration Cake";

  // Collect all images from formData
  const images: { key: string; src: string }[] = [];
  for (const key of ["picture1", "picture2", "image"]) {
    if (formData[key] && typeof formData[key] === "string" && formData[key].startsWith("data:image")) {
      images.push({ key, src: formData[key] });
    }
  }

  const InfoRow: React.FC<{ label: string; value: string | number | undefined | null; highlight?: boolean }> = ({ label, value, highlight }) => {
    if (!value && value !== 0) return null;
    const bg = highlight ? "#000" : "transparent";
    const fg = highlight ? "#fff" : "#000";
    return (
      <tr>
        <td style={{ background: bg, color: fg, fontWeight: 900, padding: "4px 6px", whiteSpace: "nowrap", verticalAlign: "top", width: "40%", borderBottom: "1px solid #ccc" }}>
          {label}:
        </td>
        <td style={{ background: bg, color: fg, padding: "4px 6px", textAlign: "left", verticalAlign: "top", borderBottom: "1px solid #ccc" }}>
          {String(value)}
        </td>
      </tr>
    );
  };

  const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
    <div style={{ fontWeight: 900, fontStyle: "italic", textDecoration: "underline", fontSize: "16px", marginTop: "8px", marginBottom: "4px" }}>
      {title}
    </div>
  );

  const DashedDivider = () => (
    <div style={{ borderBottom: "1px dashed #000", marginBottom: "6px", paddingBottom: "6px" }} />
  );

  const formatMoney = (v: any) => {
    if (v === undefined || v === null || v === "") return "";
    const n = typeof v === "number" ? v : parseFloat(String(v));
    if (isNaN(n)) return "";
    return `£${n.toFixed(2)}`;
  };

  const hasPaymentDetails =
    formData.price !== undefined ||
    formData.extra !== undefined ||
    formData.delivery !== undefined ||
    formData.total !== undefined ||
    formData.deposit !== undefined ||
    formData.balance !== undefined;

  const renderPaymentDetails = () =>
    hasPaymentDetails ? (
      <>
        <SectionHeader title="Payment Details" />
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px", marginBottom: "6px" }}>
          <tbody>
            <InfoRow label="Price" value={formatMoney(formData.price)} />
            <InfoRow label="Extra" value={formatMoney(formData.extra)} />
            <InfoRow label="Delivery" value={formatMoney(formData.delivery)} />
            <InfoRow label="Total" value={formatMoney(formData.total)} highlight />
            <InfoRow label="Deposit" value={formatMoney(formData.deposit)} />
            <InfoRow label="Balance" value={formatMoney(formData.balance)} highlight />
          </tbody>
        </table>
      </>
    ) : null;

  const renderCelebrationCake = () => {
    const size = formData.size || order.size || "";
    const shape = formData.shape || order.shape || "";
    const creamType = formData.creamType || "";
    const occasion = formData.occasion || order.occasion || "";
    const gender = formData.gender || order.gender || "";
    const messageOnCake = formData.messageOnCake || order.messageOnCake || "";
    const contactNumber = formData.contactNumber || order.contactNumber || "";
    const customerAddress = formData.customerAddress || "";
    const pictureName = formData.pictureName || order.pictureName || "";
    const pictureCode = formData.pictureCode || order.pictureCode || "";
    const code = formData.code || order.code || "";
    const quantity = formData.quantity ?? order.quantity ?? 1;
    const specialShapeRequired = formData.specialShapeRequired || "";
    const collectFrom = formData.collectFrom || formData.branch || order.clientName || order.branch || "";

    const customerName = formData.customerName || order.clientName || "";

    // Format required date with day name. Time comes from formData.requiredTime ("HH:MM").
    const formatDateWithDay = (dateStr: string | undefined, timeStr?: string) => {
      if (!dateStr) return { date: "", day: "" };
      const d = new Date(dateStr);
      const datePart = d.toLocaleDateString("en-GB", {
        weekday: "long",
        day: "2-digit",
        month: "long",
        year: "numeric",
      });
      const day = d.toLocaleDateString("en-GB", { weekday: "long" });
      const timePart = formatRequiredTime(timeStr);
      return { date: timePart ? `${datePart}, ${timePart}` : datePart, day };
    };

    const formatOrderDate = (dateStr: string | undefined) => {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      return d.toLocaleDateString("en-GB", {
        weekday: "long",
        day: "2-digit",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    };

    const requiredInfo = formatDateWithDay(order.dateRequired, formData.requiredTime);

    return (
      <>
        {/* Order Details Section */}
        <SectionHeader title="Order Details" />
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px", marginBottom: "6px" }}>
          <tbody>
            <InfoRow label="Order No." value={order.orderNumber || "-"} />
            <InfoRow label="Branch" value={branch} highlight />
            <InfoRow label="Order Date" value={formatOrderDate(order.dateOrdered)} />
            <InfoRow label="Required Date" value={requiredInfo.date} />
            <InfoRow label="Required Day" value={requiredInfo.day} />
            <InfoRow label="Collect From" value={collectFrom} highlight />
            <InfoRow label="Size" value={size} />
            <InfoRow label="Quantity" value={quantity} />
            {specialShapeRequired && <InfoRow label="Special Shape Required" value={specialShapeRequired} />}
            <InfoRow label="Shape" value={shape} highlight />
            <InfoRow label="Cream Type" value={creamType} highlight />
            <InfoRow label="Occasion" value={occasion} highlight />
            <InfoRow label="Gender" value={gender} />
            <InfoRow label="Code" value={code} />
            <InfoRow label="Picture Code" value={pictureCode} />
            <InfoRow label="Picture Name" value={pictureName} />
          </tbody>
        </table>

        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px", marginBottom: "6px" }}>
          <tbody>
            <InfoRow label="Message On Cake" value={messageOnCake} />
            <InfoRow label="Any Request or Notes" value={notes} />
          </tbody>
        </table>

        {/* Customer Section */}
        <SectionHeader title="Customer" />
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px", marginBottom: "6px" }}>
          <tbody>
            <InfoRow label="Customer Name" value={customerName} />
            <InfoRow label="Contact No" value={contactNumber} />
            <InfoRow label="Customer Address" value={customerAddress} />
            <InfoRow label="Order Taken By" value={orderTakenBy} />
            <InfoRow label="Date" value={formatDateTime(order.dateOrdered)} />
          </tbody>
        </table>

        {renderPaymentDetails()}
      </>
    );
  };

  const renderStandardOrder = () => {
    const products: { name?: string; code?: string; quantity?: number }[] = formData.products || [];

    return (
      <>
        {/* Order Info */}
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px", marginBottom: "6px" }}>
          <tbody>
            <InfoRow label="Order #" value={order.orderNumber || "-"} />
            <InfoRow label="Date" value={formatDateTime(order.dateOrdered)} />
            <InfoRow label="Required" value={formatRequiredDateTime(order.dateRequired, formData.requiredTime)} />
            <InfoRow label="Branch" value={branch} />
            <InfoRow label="Taken by" value={orderTakenBy} />
            <InfoRow label="Status" value={order.status} />
          </tbody>
        </table>
        <DashedDivider />

        {/* Products */}
        {products.length > 0 && (
          <>
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 900, fontSize: "13px", marginBottom: "4px" }}>
                <span>Item</span>
                <span>Qty</span>
              </div>
              {products.map((product, idx) => (
                <div key={idx} style={{ display: "flex", justifyContent: "space-between", fontSize: "13px" }}>
                  <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", paddingRight: "8px" }}>
                    {product.name || product.code || ""}
                  </span>
                  <span style={{ fontWeight: 900 }}>{product.quantity ?? ""}</span>
                </div>
              ))}
            </div>
            <DashedDivider />
          </>
        )}

        {/* Form data fields (for non-product dynamic orders) */}
        {products.length === 0 && Object.keys(formData).length > 0 && (
          <>
            <div style={{ fontSize: "13px" }}>
              {Object.entries(formData).map(([key, value]) => {
                if (value === undefined || value === null || value === "") return null;
                if (typeof value === "string" && value.startsWith("data:image")) return null;
                if (typeof value === "object") return null;
                const label = key.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase());
                return (
                  <div key={key} style={{ display: "flex", justifyContent: "space-between", marginBottom: "2px" }}>
                    <span><b>{label}:</b></span>
                    <span style={{ textAlign: "right", maxWidth: "50%", overflow: "hidden", textOverflow: "ellipsis" }}>{String(value)}</span>
                  </div>
                );
              })}
            </div>
            <DashedDivider />
          </>
        )}

        {/* Notes */}
        {notes && (
          <>
            <div style={{ fontSize: "13px" }}>
              <div style={{ fontWeight: 900, marginBottom: "2px" }}>Notes:</div>
              <div>{notes}</div>
            </div>
            <DashedDivider />
          </>
        )}

        {renderPaymentDetails()}
      </>
    );
  };

  return (
    <div
      className="thermal-print-page"
      style={{
        pageBreakBefore: pageBreakBefore ? "always" : "auto",
        breakBefore: pageBreakBefore ? "page" : "auto",
      }}
    >
    <div
      className="thermal-print-wrap"
      style={{
        width: "124mm",
        margin: "0 auto",
        display: "flex",
        alignItems: "flex-start",
      }}
    >
      <div className="thermal-receipt" style={{
        width: "80mm",
        flex: "0 0 80mm",
        padding: "8px",
        fontFamily: "'Courier New', monospace",
        fontSize: "14px",
        fontWeight: 900,
        color: "#000",
        background: "#fff",
        lineHeight: 1.4,
        boxSizing: "border-box",
      }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: "6px" }}>
          <div style={{ fontSize: "18px", fontWeight: 900, marginBottom: "2px" }}>
            {supplierInfo?.businessDisplayName || fallbackSupplierName || ""}
          </div>
          {supplierInfo?.businessAddress && (
            <div style={{ fontSize: "11px", marginBottom: "2px" }}>
              {supplierInfo.businessAddress}
            </div>
          )}
          <div style={{ fontSize: "11px", marginBottom: "2px" }}>
            07723 895094
          </div>
          <div style={{ fontSize: "11px", marginBottom: "2px", fontWeight: 900 }}>
            www.nicebakers.com
          </div>
          <div style={{ fontSize: "20px", fontWeight: 900, borderBottom: "1px dashed #000", paddingBottom: "6px" }}>
            {isCelebrationCake ? "CAKE ORDER FORM" : "Order Receipt"}
          </div>
        </div>

        {/* Schema-specific content */}
        {isCelebrationCake ? renderCelebrationCake() : renderStandardOrder()}

        {/* Footer */}
        <div style={{ textAlign: "center", fontSize: "11px", paddingTop: "4px" }}>
          <div>{formatDateTime(new Date().toISOString())}</div>
          <div style={{ marginTop: "4px" }}>*** Thank You ***</div>
        </div>
      </div>

      {images.length > 0 && (
        <div className="thermal-side-images" style={{
          flex: "0 0 44mm",
          alignSelf: "center",
          paddingLeft: "4mm",
          fontSize: "11px",
          boxSizing: "border-box",
        }}>
          {images.map((img) => (
            <img
              key={img.key}
              src={img.src}
              alt={img.key}
              style={{ display: "block", width: "40mm", height: "40mm", objectFit: "cover", border: "1px solid #000", marginBottom: "4px" }}
            />
          ))}
        </div>
      )}
    </div>
    </div>
  );
};

export const ThermalPrintStyles: React.FC = () => (
  <style>{`
    .thermal-receipt,
    .thermal-receipt * {
      font-weight: 900 !important;
      text-shadow: 0.4px 0 0 currentColor, -0.4px 0 0 currentColor !important;
      -webkit-text-stroke: 0.3px currentColor !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }
    @media print {
      @page {
        size: auto;
        margin: 0;
      }
      html, body {
        margin: 0 !important;
        padding: 0 !important;
        background: #fff !important;
        height: auto !important;
        min-height: auto !important;
        max-height: none !important;
        overflow: visible !important;
      }
      /* Force all ancestors of the print pages to be unclipped and
         in normal flow. React / single-spa shells often set
         height: 100vh, position: fixed, transforms, or flex that
         confine print rendering to one page. */
      body * {
        max-height: none !important;
        overflow: visible !important;
        transform: none !important;
        position: static !important;
      }
      .thermal-toolbar {
        display: none !important;
      }
      /* Each print page must be a clean block in normal flow. */
      .thermal-print-page {
        display: block !important;
        position: static !important;
        overflow: visible !important;
        width: 100% !important;
        height: auto !important;
        max-height: none !important;
        margin: 0 !important;
        padding: 0 !important;
      }
      .thermal-side-images img {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
      }
    }
  `}</style>
);

export const ThermalPrintToolbar: React.FC<{ title: string; subtitle?: string }> = ({ title, subtitle }) => (
  <div className="thermal-toolbar" style={{ background: "#f8f9fa", borderBottom: "1px solid #dee2e6", padding: "12px 24px", position: "sticky", top: 0, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
    <h5 style={{ margin: 0 }}>
      {title}
      {subtitle && <span style={{ fontSize: "13px", marginLeft: "8px", color: "#6c757d" }}>{subtitle}</span>}
    </h5>
    <div>
      <Button variant="success" size="sm" onClick={() => window.print()} className="me-2">
        <i className="bi bi-printer me-1"></i> Print
      </Button>
      <Button variant="secondary" size="sm" onClick={() => window.close()}>
        Close
      </Button>
    </div>
  </div>
);

const DeliveryNotePrint: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useRequiredAuth();
  const [order, setOrder] = useState<Order | null>(null);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [supplierInfo, setSupplierInfo] = useState<SupplierInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    Promise.all([
      ordersApi.getOrderById(parseInt(id, 10)),
      supplierApi.getMySupplier().catch(() => null),
    ])
      .then(([orderData, supplier]) => {
        setOrder(orderData);
        if (orderData.formData) {
          try {
            setFormData(JSON.parse(orderData.formData));
          } catch {
            setFormData({});
          }
        }
        if (supplier) {
          setSupplierInfo(supplier);
        }
      })
      .catch((err) => setError(err.message || "Failed to load order"))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "100vh" }}>
        <Spinner animation="border" />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="text-center p-3">
        <p className="text-danger">{error || "Order not found"}</p>
        <Button variant="secondary" size="sm" onClick={() => window.close()}>
          Close
        </Button>
      </div>
    );
  }

  const isCelebrationCake = order.type === "celebration-cake" || order.type === "Celebration Cake";

  return (
    <>
      <ThermalPrintStyles />
      <ThermalPrintToolbar
        title="Thermal Print Preview"
        subtitle={isCelebrationCake ? "(Celebration Cake)" : undefined}
      />
      <DeliveryNoteReceipt
        order={order}
        formData={formData}
        supplierInfo={supplierInfo}
        fallbackSupplierName={user.supplierName}
      />
    </>
  );
};

export default DeliveryNotePrint;
